export type DateField = "month" | "day" | "year";

export const stripNonDigits = (value: string) => {
  return value.replace(/\D/g, "");
};

/**
 * Provides intelligent input handling for month and day fields
 * Auto-completes single digits when appropriate (e.g., "2" becomes "02" for months)
 * Restarts input when typing over complete fields
 *
 * @param currentValue - Current field value
 * @param newDigit - New digit being typed
 * @param field - Whether this is a month or day field
 * @returns Formatted field value
 */
export const smartInput = (
  currentValue: string,
  newDigit: string,
  field: "month" | "day",
) => {
  const digit = parseInt(newDigit, 10);
  const maxValue = field === "month" ? 12 : 31;
  const autoCompleteThreshold = field === "month" ? 2 : 4;

  // First digit: auto-complete if it's obviously complete
  if (currentValue === "") {
    return digit >= autoCompleteThreshold
      ? digit.toString().padStart(2, "0")
      : digit.toString();
  }

  // Second digit: combine if valid, otherwise restart
  if (currentValue.length === 1) {
    const firstDigit = parseInt(currentValue, 10);
    const combinedValue = firstDigit * 10 + digit;

    if (combinedValue <= maxValue && combinedValue >= 1) {
      return combinedValue.toString().padStart(2, "0");
    }
    // Invalid combination - restart with new digit
    return digit >= autoCompleteThreshold
      ? digit.toString().padStart(2, "0")
      : digit.toString();
  }

  // Field is complete - restart with new digit
  return digit >= autoCompleteThreshold
    ? digit.toString().padStart(2, "0")
    : digit.toString();
};

/**
 * Returns the next value for a date field based on an increment.
 * - Month: cycles 1-12 with wraparound
 * - Day: cycles 1-31 with wraparound
 * - Year: clamps between 1000-9999
 *
 * @param currentValue - Current field value
 * @param field - Which date field is being modified
 * @param increment - +1 for up arrow, -1 for down arrow
 */
export const cycleValue = (
  currentValue: string,
  field: DateField,
  increment: number,
): string => {
  if (field === "month") {
    const current = parseInt(currentValue || "1", 10);
    const newValue = current + increment;

    // Wrap around: 12 -> 1, 0 -> 12
    const cycled = newValue > 12 ? 1 : newValue < 1 ? 12 : newValue;
    return String(cycled).padStart(2, "0");
  }

  if (field === "day") {
    const current = parseInt(currentValue || "1", 10);
    const newValue = current + increment;

    // Wrap around: 31 -> 1, 0 -> 31
    const cycled = newValue > 31 ? 1 : newValue < 1 ? 31 : newValue;
    return String(cycled).padStart(2, "0");
  }

  if (field === "year") {
    const current = currentValue
      ? parseInt(currentValue, 10)
      : new Date().getFullYear();
    const newValue = current + increment;

    // Clamp between 1000-9999
    const clamped = Math.min(9999, Math.max(1000, newValue));
    return String(clamped);
  }

  return currentValue;
};

type DateInputContext = {
  currentField: DateField;
  refs: {
    monthInputRef: React.RefObject<HTMLInputElement>;
    dayInputRef: React.RefObject<HTMLInputElement>;
    yearInputRef: React.RefObject<HTMLInputElement>;
  };
  values: {
    month: string;
    day: string;
    year: string;
  };
  setters: {
    setMonth: (value: string) => void;
    setDay: (value: string) => void;
    setYear: (value: string) => void;
  };
  onValueChange?: (month: string, day: string, year: string) => void;
};

/**
 * Handles horizontal navigation between date fields (left/right arrows)
 */
const handleFieldNavigation = (key: string, context: DateInputContext) => {
  const { currentField, refs } = context;
  const { monthInputRef, dayInputRef, yearInputRef } = refs;

  if (key === "ArrowLeft") {
    if (currentField === "day") monthInputRef.current?.focus();
    if (currentField === "year") dayInputRef.current?.focus();
  } else if (key === "ArrowRight") {
    if (currentField === "month") dayInputRef.current?.focus();
    if (currentField === "day") yearInputRef.current?.focus();
  }
};

/**
 * Handles value cycling within a field (up/down arrows)
 */
const handleValueCycling = (increment: number, context: DateInputContext) => {
  const { currentField, values, setters, onValueChange } = context;
  const { month, day, year } = values;

  const fieldSetters = {
    month: () => {
      const newMonth = cycleValue(month, "month", increment);
      setters.setMonth(newMonth);
      onValueChange?.(newMonth, day, year);
    },
    day: () => {
      const newDay = cycleValue(day, "day", increment);
      setters.setDay(newDay);
      onValueChange?.(month, newDay, year);
    },
    year: () => {
      const newYear = cycleValue(year, "year", increment);
      setters.setYear(newYear);
      onValueChange?.(month, day, newYear);
    },
  };

  fieldSetters[currentField]();
};

/**
 * Handles keyboard navigation between date input fields and cycling values
 * - Left/Right arrows: move between fields (month <-> day <-> year)
 * - Up/Down arrows: increment/decrement field values with cycling/clamping
 */
export const handleFieldKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>,
  context: DateInputContext,
) => {
  const isArrowKey = [
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
  ].includes(e.key);

  if (!isArrowKey) return;

  e.preventDefault();

  if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
    handleFieldNavigation(e.key, context);
  } else {
    const increment = e.key === "ArrowUp" ? 1 : -1;
    handleValueCycling(increment, context);
  }
};
